import { createContext, useContext } from 'react';
import { useAuth } from '@guildeducationinc/guild-auth';
import config, { MilestoneConfig } from '@src/components/LearningLifecycles/config';
import useGetUserFundingLimit from '@src/hooks/useGetUserFundingLimit';
import useEmployerService from '@src/hooks/useEmployerService';
import useGetEmployerConfig from '@src/hooks/useGetEmployerConfig';
import { useMemberTraitsContext } from './MemberTraitsContext';
import { useLearningLifecyclesContext } from './LearningLifecyclesContext';

export enum MilestoneKey {
  ApplicantCreated = 'applicantCreated',
  ApplicantSubmitted = 'applicantSubmitted',
  ApplicantDeferred = 'applicantDeferred',
  CompleteYourSignup = 'completeYourSignup',
  CompleterActualCompletionDateSaved = 'completerActualCompletionDateSaved',
  CompleterCareerAccelerator = 'careerAccelerator',
  CompleterExpectedCompletionDatePassed = 'completerExpectedCompletionDatePassed',
  CompleterFindNextProgram = 'completerFindNextProgram',
  LearnerBooksAndFeesReimbursement = 'learnerBooksAndFeesReimbursement',
  LearnerCareerAccelerator = 'careerAccelerator',
  LearnerConfirmExpectedCompletionDate = 'learnerConfirmExpectedCompletionDate',
  LearnerLearningPartnerTasks = 'learnerLearningPartnerTasks',
  SubmittedExpectedCompletionDate = 'submittedExpectedCompletionDate',
  LearnerSubmitExpectedCompletionDate = 'learnerSubmitExpectedCompletionDate',
  PreApplicantBachelorsWarning = 'preApplicantBachelorsWarning',
  PreApplicantCatalog = 'preApplicantCatalog',
  PreApplicantCatalogWithRecs = 'preApplicantCatalogWithRecs',
  PreApplicantFindFunding = 'preApplicantFindFunding',
  PreApplicantNoPlanData = 'preApplicantNoPlanData',
  PreApplicantProgramQuiz = 'preApplicantProgramQuiz',
  PreApplicantRequestReimbursement = 'preApplicantRequestReimbursement',
  PreApplicantRequestSponsorship = 'preApplicantRequestSponsorship',
  TransitionRequestReimbursement = 'transitionRequestReimbursement',
  TransitionRequestSponsorship = 'transitionRequestSponsorship',
  TransitionStartProgramApplication = 'transitionStartProgramApplication',
}

export interface MilestoneContext {
  applicantApplicationDeferred?: MilestoneConfig;
  applicantApplicationSubmitted?: MilestoneConfig;
  applicantCompleteApplication?: MilestoneConfig;
  completeSchoolProcess?: MilestoneConfig;
  completeYourSignup?: MilestoneConfig;
  completerCareerAccelerator?: MilestoneConfig;
  completerExpectedCompletionDatePassed?: MilestoneConfig;
  completerFindNextProgram?: MilestoneConfig;
  completerActualCompletionDateSaved?: MilestoneConfig;
  learnerBooksAndFeesReimbursement?: MilestoneConfig;
  learnerCareerAccelerator?: MilestoneConfig;
  learnerConfirmExpectedCompletionDate?: MilestoneConfig;
  submittedExpectedCompletionDate?: MilestoneConfig;
  learnerSubmitExpectedCompletionDate?: MilestoneConfig;
  preApplicantBachelorsWarning?: MilestoneConfig;
  preApplicantCatalog?: MilestoneConfig;
  preApplicantCatalogWithRecs?: MilestoneConfig;
  preApplicantFindFunding?: MilestoneConfig;
  preApplicantNoPlanData?: MilestoneConfig;
  preApplicantProgramQuiz?: MilestoneConfig;
  preApplicantRequestReimbursement?: MilestoneConfig;
  preApplicantRequestSponsorship?: MilestoneConfig;
  transitionRequestReimbursement?: MilestoneConfig;
  transitionRequestSponsorship?: MilestoneConfig;
  transitionStartProgramApplication?: MilestoneConfig;
}

const MilestoneContext = createContext<MilestoneContext | undefined>(undefined);

interface MilestoneProviderProps {
  children: React.ReactNode;
}

const MilestoneProvider = ({ children }: MilestoneProviderProps) => {
  return <MilestoneContext.Provider value={config}>{children}</MilestoneContext.Provider>;
};

const useMilestone = (key?: MilestoneKey, order?: number) => {
  const context = useContext(MilestoneContext);
  const { userFundingLimit } = useGetUserFundingLimit();
  const { user } = useAuth();
  const { employer } = useEmployerService(user?.employerId);
  const { memberTraits } = useMemberTraitsContext();
  const { employerConfig } = useGetEmployerConfig();
  const { currentLearningLifecycle } = useLearningLifecyclesContext();

  if (context === undefined) {
    throw new Error('The useMilestone hook must be used within a MilestoneProvider.');
  }

  const milestone = key ? context[key] : undefined;

  let variables = {};

  if (milestone?.useEmployerService) {
    variables = { ...variables, employerName: employer?.name };
  }

  if (milestone?.useMemberTraits) {
    variables = { ...variables, ...memberTraits };
  }

  if (milestone?.useUserFundingLimit) {
    variables = { ...variables, userFundingLimit };
  }

  if (milestone?.useEmployerConfig) {
    variables = { ...variables, employerConfig };
  }

  if (milestone?.useLearningLifecycleService) {
    variables = { ...variables, currentLearningLifecycle };
  }

  if (order) {
    variables = { ...variables, order };
  }

  return milestone?.content(variables);
};

export { MilestoneProvider, useMilestone };
